// export default AuthForm;
import React, { useState, useEffect } from "react";
import { Box, Typography, Container, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { styles } from "./AuthPage.styles";
import { auth, actionCodeSettings } from "../../config/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import SignInSignUp from "./SignInSignUp";
import VerifyEmail from "./VerifyEmail";
import ForgotPassword from "./ForgotPassword";
import colors from "../../constants/colors";
import WebsiteBranding from "../common/WebsiteBranding";
import { effigenColor } from "../../constants/brandLogos";

const AuthPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVerificationPage, setShowVerificationPage] = useState(false);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [navigationPath, setNavigationPath] = useState("");
  const [skipVerificationCheck, setSkipVerificationCheck] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check for verification state on mount
  useEffect(() => {
    if (!skipVerificationCheck) {
      const needsVerification = localStorage.getItem("needsVerification");
      const storedEmail = localStorage.getItem("pendingVerificationEmail");
      if (needsVerification === "true" && storedEmail) {
        setShowVerificationPage(true);
        setEmail(storedEmail);
      }
    }
  }, [skipVerificationCheck]);

  useEffect(() => {
    if (shouldNavigate) {
      navigate(navigationPath);
      setShouldNavigate(false);
    }
  }, [shouldNavigate, navigationPath, navigate]);

  const getFirebaseErrorMessage = (error) => {
    const errorCode = error.code;
    const errorMessage = error.message;

    if (errorCode === "auth/password-does-not-meet-requirements") {
      // Extracting password requirements from the error message
      const requirementRegex = /\[(.*?)\]/;
      const match = errorMessage.match(requirementRegex);

      if (match) {
        const requirements = match[1]
          .split(", ")
          .map((req, index) => <div key={index}>• {req.trim()}</div>);

        return (
          <div>
            <strong>Password does not meet the following requirements:</strong>
            {requirements}
          </div>
        );
      }
    }
    const errorMessages = {
      "auth/email-already-in-use":
        "The email address is already in use. Please use a different one.",
      "auth/invalid-login-credentials":
        "Invalid email or password. Please try again.",
      "auth/invalid-email":
        "The email address is not valid. Please enter a correct email.",
      "auth/user-not-found":
        "No user found with this email. Please sign up first.",
      "auth/wrong-password": "Incorrect password. Please try again.",
      "auth/weak-password":
        "Password is too weak. Please use a stronger password (at least 6 characters).",
      "auth/network-request-failed":
        "Network error. Please check your internet connection.",
      "auth/invalid-credential":
        "Invalid email or password. Please signup or try again.",
      "auth/too-many-requests": "Too many attempts. Please try again later.",
      "auth/missing-email": "Please enter your email address.",
      "auth/operation-not-allowed":
        "Password reset is not enabled. Please contact support.",
    };

    console.log(errorCode);

    // If we have a specific error message for this code, use it
    if (errorMessages[errorCode]) {
      return errorMessages[errorCode];
    }

    // Otherwise, return the actual error message from Firebase
    return `Error: ${errorMessage} (Code: ${errorCode})`;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError("");
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Check passwords only when creating account
    if (activeTab === 1 && confirmPassword !== password) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      if (activeTab === 0) {
        // Login
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );

        // Check if email is verified
        if (!userCredential.user.emailVerified) {
          // Sign out the user since they need to verify their email
          await auth.signOut();
          localStorage.removeItem("token");
          localStorage.setItem("needsVerification", "true");
          localStorage.setItem("pendingVerificationEmail", email);
          localStorage.setItem("pendingVerificationPassword", password); // Store password temporarily

          setShowVerificationPage(true);
          setVerificationMessage("Please verify your email before logging in.");
          setLoading(false);
          return;
        }

        // Get the ID token
        const idToken = await userCredential.user.getIdToken();
        // Store the token
        localStorage.setItem("token", idToken);
        localStorage.removeItem("needsVerification");
        localStorage.removeItem("pendingVerificationEmail");
        localStorage.removeItem("pendingVerificationPassword"); // Clean up stored password
        navigate("/");
      } else {
        // Signup
        const output = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        // Send verification email
        await sendEmailVerification(output.user, actionCodeSettings);

        // Sign out the user since they need to verify their email
        await auth.signOut();
        localStorage.removeItem("token");
        localStorage.setItem("needsVerification", "true");
        localStorage.setItem("pendingVerificationEmail", email);
        localStorage.setItem("pendingVerificationPassword", password); // Store password temporarily

        // Show verification page
        setShowVerificationPage(true);
        setVerificationMessage(
          "Please check your email for verification link. You must verify your email before logging in."
        );
      }
    } catch (err) {
      setError(getFirebaseErrorMessage(err));
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      const storedEmail = localStorage.getItem("pendingVerificationEmail");
      const storedPassword = localStorage.getItem(
        "pendingVerificationPassword"
      );

      if (!storedEmail || !storedPassword) {
        setError("Unable to resend verification. Please try signing up again.");
        setLoading(false);
        return;
      }

      // Sign in temporarily to send verification email
      const userCredential = await signInWithEmailAndPassword(
        auth,
        storedEmail,
        storedPassword
      );

      await sendEmailVerification(userCredential.user);

      // Sign out again
      await auth.signOut();
      localStorage.removeItem("token");
      localStorage.setItem("needsVerification", "true");

      setVerificationMessage(
        "Verification email sent! Please check your inbox."
      );
    } catch (err) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (email) => {
    setError("");
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      setResetSent(true);
      setError(""); // Ensure error is cleared
      return true;
    } catch (err) {
      setError(getFirebaseErrorMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Container
        maxWidth="sm"
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <WebsiteBranding logoURL={effigenColor} />
        {showVerificationPage ? (
          <VerifyEmail
            handleResendVerification={handleResendVerification}
            loading={loading}
            error={error}
            verificationMessage={verificationMessage}
            onBackToSignIn={() => {
              setSkipVerificationCheck(true);
              setShowVerificationPage(false);
              setActiveTab(0);
            }}
          />
        ) : showForgotPassword ? (
          <ForgotPassword
            onBackToSignIn={() => {
              setShowForgotPassword(false);
              setResetSent(false);
              setError("");
            }}
            handleForgotPassword={handleForgotPassword}
            loading={loading}
            error={error}
          />
        ) : (
          <SignInSignUp
            handleSubmit={handleSubmit}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            error={error}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            showPassword={showPassword}
            handleClickShowPassword={handleClickShowPassword}
            handleMouseDownPassword={handleMouseDownPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            showConfirmPassword={showConfirmPassword}
            handleClickShowConfirmPassword={handleClickShowConfirmPassword}
            loading={loading}
            onForgotPassword={() => setShowForgotPassword(true)}
            onNavigateHome={() => navigate("/")}
          />
        )}
      </Container>
    </Box>
  );
};

export default AuthPage;
